import { useState, useRef, useCallback, useEffect } from "react";

export function useTts({ onStatusChange, isNepali, enabled = true, hoverEnabled = true } = {}) {
  const synthRef = useRef(null);
  const utteranceRef = useRef(null);
  const [speaking, setSpeaking] = useState(false);
  const [voices, setVoices] = useState([]);
  const hoverTimerRef = useRef(null);
  const lastUrlRef = useRef(typeof window !== "undefined" ? window.location.href : "");
  const enabledRef = useRef(enabled);
  const hoverEnabledRef = useRef(hoverEnabled);

  enabledRef.current = enabled;
  hoverEnabledRef.current = hoverEnabled;

  useEffect(() => {
    if (typeof window === "undefined") return;
    synthRef.current = window.speechSynthesis;

    const update = () => {
      setVoices(synthRef.current.getVoices());
    };
    update();
    if (synthRef.current.onvoiceschanged !== undefined) {
      synthRef.current.onvoiceschanged = update;
    }
    return () => {
      if (synthRef.current) {
        synthRef.current.cancel();
      }
    };
  }, []);

  useEffect(() => {
    const current = typeof window !== "undefined" ? window.location.href : "";
    if (current !== lastUrlRef.current) {
      lastUrlRef.current = current;
      if (synthRef.current) {
        synthRef.current.cancel();
      }
      setSpeaking(false);
    }
  });

  const getLang = useCallback(() => {
    return isNepali ? "ne-NP" : "en-US";
  }, [isNepali]);

  const pickVoice = useCallback(
    (lang) => {
      const langPrefs =
        lang === "ne-NP" ? ["ne-NP", "hi-IN", "en-US"] : ["en-US"];
      for (const l of langPrefs) {
        const match = voices.find((v) => v.lang.startsWith(l));
        if (match) return match;
      }
      return voices[0] || null;
    },
    [voices]
  );

  const stopSpeech = useCallback(() => {
    if (synthRef.current) {
      synthRef.current.cancel();
    }
    setSpeaking(false);
    utteranceRef.current = null;
    if (onStatusChange) onStatusChange("stopped");
  }, [onStatusChange]);

  useEffect(() => {
    return () => {
      if (synthRef.current) synthRef.current.cancel();
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => {
      if (synthRef.current) synthRef.current.cancel();
      setSpeaking(false);
    };
    window.addEventListener("popstate", handler);
    window.addEventListener("hashchange", handler);
    return () => {
      window.removeEventListener("popstate", handler);
      window.removeEventListener("hashchange", handler);
    };
  }, []);

  const speak = useCallback(
    (text) => {
      if (!synthRef.current || !text) return;
      if (!enabledRef.current) return;
      synthRef.current.cancel();
      const lang = getLang();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1;
      utterance.lang = lang;
      const voice = pickVoice(lang);
      if (voice) utterance.voice = voice;

      utterance.onstart = () => {
        setSpeaking(true);
        if (onStatusChange) onStatusChange("speaking");
      };
      utterance.onend = () => {
        setSpeaking(false);
        utteranceRef.current = null;
        if (onStatusChange) onStatusChange("stopped");
      };
      utterance.onerror = (e) => {
        if (e.error !== "canceled") {
          setSpeaking(false);
          utteranceRef.current = null;
          if (onStatusChange) onStatusChange("stopped");
        }
      };

      utteranceRef.current = utterance;
      synthRef.current.speak(utterance);
    },
    [getLang, pickVoice, onStatusChange]
  );

  const cancelSpeech = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (synthRef.current) {
      synthRef.current.cancel();
    }
    setSpeaking(false);
    utteranceRef.current = null;
  }, []);

  const getElementText = useCallback((el) => {
    if (!el || el.nodeType !== 1) return "";
    if (el.getAttribute("aria-hidden") === "true") return "";
    if (el.closest('[aria-hidden="true"]')) return "";
    if (el.closest(".a11y-floating-panel, .a11y-side-panel, .a11y-toolbar")) return "";

    if (el.tagName === "IMG") {
      return el.getAttribute("alt") || el.getAttribute("aria-label") || "";
    }

    const ariaLabel =
      el.getAttribute("aria-label") ||
      el.getAttribute("aria-labelledby");
    if (ariaLabel) {
      if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
        const val = el.value;
        return val ? `${ariaLabel}: ${val}` : ariaLabel;
      }
      return ariaLabel;
    }
    const skipTags = ["SCRIPT", "STYLE", "NOSCRIPT", "SVG", "path", "circle", "rect"];
    if (skipTags.includes(el.tagName)) return "";
    if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
      const lab = el.closest("label") || document.querySelector(`label[for="${el.id}"]`);
      const labelText = lab ? lab.textContent.replace(/\s+/g, " ").trim() : "";
      const val = el.value;
      if (labelText && val) return `${labelText}: ${val}`;
      if (labelText) return labelText;
      if (val) return val;
    }
    let text = el.textContent.replace(/\s+/g, " ").trim();
    el.querySelectorAll("img").forEach((img) => {
      const alt = img.getAttribute("alt");
      if (alt) text += " " + alt;
    });
    return text;
  }, []);

  const speakRef = useRef(speak);
  speakRef.current = speak;
  const getElementTextRef = useRef(getElementText);
  getElementTextRef.current = getElementText;

  useEffect(() => {
    if (!enabled) {
      cancelSpeech();
      return;
    }
    if (typeof window === "undefined" || typeof document === "undefined") return;
    const handler = (e) => {
      const text = getElementTextRef.current(e.target);
      if (text) speakRef.current(text);
    };
    document.addEventListener("focusin", handler);
    return () => {
      document.removeEventListener("focusin", handler);
    };
  }, [enabled, cancelSpeech]);

  useEffect(() => {
    if (!hoverEnabled) {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
      return;
    }
    if (typeof window === "undefined" || typeof document === "undefined") return;
    const handler = (e) => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = setTimeout(() => {
        const text = getElementTextRef.current(e.target);
        if (text) speakRef.current(text);
      }, 300);
    };
    document.addEventListener("mouseover", handler);
    return () => {
      document.removeEventListener("mouseover", handler);
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
    };
  }, [hoverEnabled, cancelSpeech]);

  useEffect(() => {
    if (!enabled) {
      cancelSpeech();
      return;
    }
    if (typeof window === "undefined" || typeof document === "undefined") return;
    const handler = (e) => {
      const text = getElementTextRef.current(e.target);
      if (text) speakRef.current(text);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [enabled, cancelSpeech]);

  return {
    speak,
    stop: stopSpeech,
    speaking,
    stopSpeech,
    hoverRead: true,
    readOnFocus: true,
  };
}
